import { StorageKeys } from 'common/storage/constants';
import { isNil } from 'lodash';
import { useCallback, useEffect } from 'react';
import { CurrentUserFieldsFragment, PromoCodeFieldsFragment } from 'src/gqlReactTypings.generated.d';
import { usePromotionalsHook } from 'src/promotionals/usePromotionalsHook';
import { useCurrentUser } from 'src/shared/hooks/useCurrentUserHook';
import { useHistory } from 'src/shared/hooks/useHistory';
import { useLocation } from 'src/shared/hooks/useLocation';
import { useModal } from 'src/shared/hooks/useModal';
import { ConsumerLoginRoutes } from 'src/shared/Routes';
import { useLocalStorage } from 'src/shared/storage/Storage';
import { locationInRoutes } from 'src/shared/utils/RouteUtilities';
import { useDispatch, useSelector } from 'src/store';
import { hideLoginModal, showLoginModal } from 'src/store/login';
import { useHideLoginOrSignUpModal } from './useHideLoginOrSignUpModal';

export const SUBSCRIPTION_INTAKE_ENTRY_POINT = 'subscription_intake';
export const LOGIN_PAGE_ENTRY_POINT = 'login_page';
export const TABLERUN_STOREFRONT_ENTRY_POINT = 'tablerun_storefront';

type ShowProps = {
  signUpPromoCode?: PromoCodeFieldsFragment | null;
  onHideModalKey?: string;
  isClosable?: boolean;
  requireZipCode?: boolean;
  entryHeader?: string;
  signUpEntryPoint?: string | null;
  preventReloadOnLogin?: boolean;
  onSuccessfulLogin?: (currentUser: CurrentUserFieldsFragment) => void;
  signUpCTA?: string | null;
  continueCTA?: string | null;
  loginCTA?: string | null;
  hideBanner?: boolean;
  hideGuestOption?: boolean;
  showMealPlanHeader?: boolean;
  isFreshIntake?: boolean;
};

type UseShowLoginOrSignUpResult = Pick<ReturnType<typeof useModal>, 'hide' | 'isShowing'> & {
  shouldShowGoogleOTL: boolean;
  show: (props?: ShowProps) => void;
  signUpPromoCode: PromoCodeFieldsFragment | null;
  isClosable: boolean;
  requireZipCode?: boolean;
  signUpEntryPoint?: string | null;
  entryHeader: string | null;
  preventReloadOnLogin?: boolean | null;
  onSuccessfulLogin?: ((currentUser: CurrentUserFieldsFragment) => void) | null;
  signUpCTA: string | null;
  continueCTA: string | null;
  loginCTA: string | null;
  hideBanner: boolean | null;
  hideGuestOption: boolean | null;
  showMealPlanHeader: boolean | null;
  isFreshIntake: boolean;
};

export const useShowLoginOrSignUpModal = (): UseShowLoginOrSignUpResult => {
  const [currentUser, currentUserIsLoading] = useCurrentUser();
  const { code } = usePromotionalsHook();
  const [, setStorageKey] = useLocalStorage(StorageKeys.LOGIN_ON_EXPLORE_MODAL, false);
  const { onHideModal } = useHideLoginOrSignUpModal();
  const isLoginModalShowing = useSelector((state) => state.login.isLoginModalShowing);
  const signUpPromoCode = useSelector((state) => {
    if (state.login.options?.signUpPromoCode) {
      return state.login.options.signUpPromoCode;
    }
    if (code) {
      return code;
    }
    return null;
  });
  const isClosable = useSelector((state) => state.login.options?.isClosable ?? true);
  const requireZipCode = useSelector((state) => state.login.options?.requireZipCode ?? false);
  const entryHeader = useSelector((state) => state.login.options?.entryHeader ?? null);
  const storedSignUpEntryPoint = useSelector((state) => state.login.options?.signUpEntryPoint ?? null);
  const currentLocation = useLocation();
  /**
   * /login and /register render the content directly, so nothing dispatches `showLoginModal` and
   * every event there reported a null entry point. A dispatched value still wins.
   */
  const signUpEntryPoint =
    storedSignUpEntryPoint ?? (locationInRoutes(currentLocation, ConsumerLoginRoutes) ? LOGIN_PAGE_ENTRY_POINT : null);
  const isFreshIntake = useSelector((state) => state.login.options?.isFreshIntake ?? false);
  const preventReloadOnLogin = useSelector((state) => state.login.options?.preventReloadOnLogin ?? null);
  const onSuccessfulLogin = useSelector((state) => state.login.options?.onSuccessfulLogin ?? null);
  const signUpCTA = useSelector((state) => state.login.options?.signUpCTA ?? null);
  const continueCTA = useSelector((state) => state.login.options?.continueCTA ?? null);
  const loginCTA = useSelector((state) => state.login.options?.loginCTA ?? null);
  const hideBanner = useSelector((state) => state.login.options?.hideBanner ?? null);
  const hideGuestOption = useSelector((state) => state.login.options?.hideGuestOption ?? null);
  const showMealPlanHeader = useSelector((state) => state.login.options?.showMealPlanHeader ?? null);

  const dispatch = useDispatch();

  const hide = useCallback(() => {
    onHideModal();
    dispatch(hideLoginModal());
    setStorageKey(true);
  }, [dispatch, onHideModal]);

  const show = useCallback(
    ({
      signUpPromoCode = null,
      onHideModalKey,
      isClosable,
      requireZipCode,
      entryHeader,
      signUpEntryPoint,
      preventReloadOnLogin,
      onSuccessfulLogin,
      signUpCTA,
      continueCTA,
      loginCTA,
      hideBanner,
      hideGuestOption,
      showMealPlanHeader,
      isFreshIntake,
    }: ShowProps = {}) => {
      dispatch(
        showLoginModal({
          signUpPromoCode,
          onHideModalKey,
          isClosable,
          requireZipCode,
          entryHeader,
          signUpEntryPoint,
          preventReloadOnLogin,
          onSuccessfulLogin,
          signUpCTA,
          continueCTA,
          loginCTA,
          hideBanner,
          hideGuestOption,
          showMealPlanHeader,
          isFreshIntake,
        })
      );
    },
    [dispatch]
  );

  // Hide the modal if the user switches pages (via browser buttons, for example).
  // Only react to pathname changes — same-page query-param updates (e.g. `replaceIn`)
  // must not close the modal, or the meal-plan intake's default-tab write races the
  // open and dismisses the sign-up wall.
  const history = useHistory();
  useEffect(() => {
    let previousPathname = history.location.pathname;
    const unlistenCallback = history.listen((location) => {
      const pathnameChanged = location.pathname !== previousPathname;
      previousPathname = location.pathname;
      if (pathnameChanged && isLoginModalShowing) {
        // Don't use callback, since we don't want to set the storage key
        dispatch(hideLoginModal());
      }
    });
    return unlistenCallback;
  }, [dispatch, history, isLoginModalShowing]);

  const isShowing = isLoginModalShowing && !currentUserIsLoading && isNil(currentUser);
  return {
    show,
    hide,
    isShowing,
    shouldShowGoogleOTL: !isShowing,
    signUpPromoCode,
    isClosable,
    requireZipCode,
    signUpEntryPoint,
    isFreshIntake,
    entryHeader,
    preventReloadOnLogin,
    onSuccessfulLogin,
    signUpCTA,
    continueCTA,
    loginCTA,
    hideBanner,
    hideGuestOption,
    showMealPlanHeader,
  };
};
